import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useImpersonationStore,
  isImpersonationActive,
  isImpersonationExpired,
} from "@/store/impersonation";
import { getRealUser } from "@/lib/session";
import { api } from "@/lib/api";
import { useAppStore } from "@/store/auth";

// Top-of-page banner shown whenever an admin is impersonating another
// user. Sticks at the very top, can't be dismissed without exiting.
//
// Click "Exit" → POST /impersonate/end, clear store, clear React Query
// cache (so the next render fetches data as the admin again), redirect
// to home.

export function ImpersonationBanner() {
  const { sessionId, target, startedAt, maxDurationSeconds, clear } =
    useImpersonationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const realUser = getRealUser();
  const resetAccountStore = useAppStore((s) => s.logout);

  // Tick once a second so the countdown updates.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!target) return;
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, [target]);

  // Auto-exit if expired.
  useEffect(() => {
    if (target && isImpersonationExpired()) {
      handleExit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, sessionId]);

  if (!target) return null;
  // Don't render banner if expired — handleExit will have cleared it,
  // but render-cycle guard.
  if (!isImpersonationActive() && !isImpersonationExpired()) return null;

  const expired = isImpersonationExpired();
  const remainingSec = startedAt
    ? Math.max(0, maxDurationSeconds - Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000))
    : 0;
  const mins = Math.floor(remainingSec / 60);
  const secs = remainingSec % 60;

  async function handleExit(silent = false) {
    try {
      await api.endImpersonation(sessionId);
    } catch {
      // Even if the server call fails, clear locally so the admin
      // isn't trapped in a broken impersonation state.
    }
    clear();
    // Clear the account store too — the impersonation target's
    // selected account shouldn't persist back to the admin's session.
    try {
      resetAccountStore();
    } catch {
      // ignore
    }
    // Nuke the query cache so the next render refetches as admin.
    queryClient.clear();
    if (!silent) {
      navigate("/admin");
    } else {
      // Silent (expired) — drop to home without a forced nav so the
      // user sees where they were.
      navigate("/");
    }
  }

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 w-full bg-amber-500 text-amber-950 border-b-2 border-amber-700 shadow-md"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
        <span className="text-lg" aria-hidden>👁️</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">
            Viewing as{" "}
            <span className="font-bold">
              {target.displayName || target.username}
            </span>
            <span className="ml-2 text-xs font-normal opacity-80">
              ({target.role}{target.accountName ? ` · ${target.accountName}` : ""})
            </span>
          </div>
          <div className="text-xs opacity-80">
            {realUser && (
              <>signed in as {realUser.displayName || realUser.username} · </>
            )}
            {expired ? (
              <span className="font-medium">Session expired — exit to continue</span>
            ) : (
              <>
                read-only · {mins}:{secs.toString().padStart(2, "0")} remaining
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => handleExit(false)}
          className="px-3 py-1.5 rounded-lg bg-amber-950 text-amber-50 text-sm font-medium hover:bg-amber-900 transition-colors"
        >
          ← Exit View As
        </button>
      </div>
    </div>
  );
}
